import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { eventsAPI, casesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Switch } from '../components/ui/switch';
import { 
  Plus, 
  Edit,
  Trash2,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Gavel,
  Bell,
  Users,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, startOfMonth, endOfMonth, isSameDay } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

const CalendarPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    event_type: 'court_session',
    case_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    time: '',
    location: '',
    description: '',
    reminder: false
  });

  const eventTypes = [
    { value: 'court_session', label: t('courtSession'), icon: Gavel, color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
    { value: 'deadline', label: t('deadline'), icon: AlertCircle, color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
    { value: 'meeting', label: t('meeting'), icon: Users, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
    { value: 'reminder', label: t('reminder'), icon: Bell, color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, casesRes] = await Promise.all([
        eventsAPI.getAll(),
        casesAPI.getAll()
      ]);
      setEvents(eventsRes.data);
      setCases(casesRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        case_id: formData.case_id || null
      };
      
      if (editingEvent) {
        await eventsAPI.update(editingEvent.id, data);
        toast.success(language === 'ar' ? 'تم تحديث الحدث' : 'Event updated');
      } else {
        await eventsAPI.create(data);
        toast.success(language === 'ar' ? 'تم إضافة الحدث' : 'Event added');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(t('error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد؟' : 'Are you sure?')) return;
    try {
      await eventsAPI.delete(id);
      toast.success(language === 'ar' ? 'تم حذف الحدث' : 'Event deleted');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('error'));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      event_type: 'court_session',
      case_id: '',
      date: format(selectedDate, 'yyyy-MM-dd'),
      time: '',
      location: '',
      description: '',
      reminder: false
    });
    setEditingEvent(null);
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      event_type: event.event_type,
      case_id: event.case_id || '',
      date: event.date,
      time: event.time || '',
      location: event.location || '',
      description: event.description || '',
      reminder: event.reminder || false
    });
    setDialogOpen(true);
  };

  const openNewEventDialog = (date) => {
    setSelectedDate(date);
    setFormData(prev => ({
      ...prev,
      date: format(date, 'yyyy-MM-dd')
    }));
    setDialogOpen(true);
  };

  const getEventType = (type) => {
    return eventTypes.find(et => et.value === type) || eventTypes[0];
  };

  const getCaseName = (caseId) => {
    const caseItem = cases.find(c => c.id === caseId);
    return caseItem ? caseItem.case_number : null;
  };

  // Get events for selected date
  const selectedDateEvents = events.filter(e => 
    isSameDay(parseISO(e.date), selectedDate)
  );

  // Get dates with events for calendar highlighting
  const datesWithEvents = events.map(e => parseISO(e.date));

  const dateLocale = language === 'ar' ? ar : enUS;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="calendar-page">
      <Header title={t('calendar')} />
      
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <Card className="lg:col-span-2" data-testid="calendar-widget">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">{t('calendar')}</CardTitle>
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-accent gap-2" data-testid="add-event-btn">
                    <Plus className="w-4 h-4" />
                    {t('addEvent')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>
                      {editingEvent ? t('edit') : t('addEvent')}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>{t('eventTitle')} *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        required
                        data-testid="event-title-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{t('eventType')} *</Label>
                      <Select 
                        value={formData.event_type} 
                        onValueChange={(v) => setFormData({...formData, event_type: v})}
                      >
                        <SelectTrigger data-testid="event-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {eventTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-2">
                                <type.icon className="w-4 h-4" />
                                {type.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('eventDate')} *</Label>
                        <Input
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData({...formData, date: e.target.value})}
                          required
                          data-testid="event-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('eventTime')}</Label>
                        <Input
                          type="time"
                          value={formData.time}
                          onChange={(e) => setFormData({...formData, time: e.target.value})}
                          data-testid="event-time-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{language === 'ar' ? 'القضية المرتبطة' : 'Related Case'}</Label>
                      <Select 
                        value={formData.case_id || "none"} 
                        onValueChange={(v) => setFormData({...formData, case_id: v === "none" ? "" : v})}
                      >
                        <SelectTrigger data-testid="event-case-select">
                          <SelectValue placeholder={language === 'ar' ? 'اختر قضية' : 'Select case'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{language === 'ar' ? 'بدون قضية' : 'No case'}</SelectItem>
                          {cases.map(c => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.case_number} - {c.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('location')}</Label>
                      <Input
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        data-testid="event-location-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{t('description')}</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        rows={2}
                        data-testid="event-description-input"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>{t('reminder')}</Label>
                      <Switch
                        checked={formData.reminder}
                        onCheckedChange={(checked) => setFormData({...formData, reminder: checked})}
                        data-testid="event-reminder-switch"
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        {t('cancel')}
                      </Button>
                      <Button type="submit" className="btn-accent" data-testid="event-submit-btn">
                        {t('save')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  locale={dateLocale}
                  className="rounded-md border"
                  modifiers={{
                    hasEvent: datesWithEvents
                  }}
                  modifiersStyles={{
                    hasEvent: {
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                      textDecorationColor: 'hsl(var(--accent))',
                      textUnderlineOffset: '4px'
                    }
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Events List */}
          <Card data-testid="events-list">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                {format(selectedDate, 'PPP', { locale: dateLocale })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedDateEvents.length > 0 ? (
                <div className="space-y-3">
                  {selectedDateEvents.map(event => {
                    const eventType = getEventType(event.event_type);
                    const EventIcon = eventType.icon;
                    return (
                      <div 
                        key={event.id}
                        className="p-3 rounded-sm border border-border hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-sm ${eventType.color.split(' ')[0]}`}>
                              <EventIcon className={`w-4 h-4 ${eventType.color.split(' ').slice(1).join(' ')}`} strokeWidth={1.5} />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{event.title}</p>
                              <Badge className={`${eventType.color} text-xs mt-1`}>
                                {eventType.label}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => openEditDialog(event)}
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-7 w-7 text-destructive"
                              onClick={() => handleDelete(event.id)}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                          {event.time && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {event.time}
                            </div>
                          )}
                          {event.location && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </div>
                          )}
                          {event.case_id && (
                            <div className="flex items-center gap-1">
                              <Gavel className="w-3 h-3" />
                              {getCaseName(event.case_id)}
                            </div>
                          )}
                        </div>
                        {event.description && (
                          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                            {event.description}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CalendarIcon className="w-10 h-10 mx-auto text-muted-foreground mb-3" strokeWidth={1} />
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? 'لا توجد أحداث في هذا اليوم' : 'No events on this day'}
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={() => openNewEventDialog(selectedDate)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('addEvent')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Events */}
        <Card className="mt-6" data-testid="upcoming-events-list">
          <CardHeader>
            <CardTitle className="text-lg">{t('upcomingEvents')}</CardTitle>
          </CardHeader>
          <CardContent>
            {events.filter(e => new Date(e.date) >= new Date()).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {events
                  .filter(e => new Date(e.date) >= new Date())
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .slice(0, 8)
                  .map(event => {
                    const eventType = getEventType(event.event_type);
                    const EventIcon = eventType.icon;
                    return (
                      <div 
                        key={event.id}
                        className="p-3 rounded-sm border border-border hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => setSelectedDate(parseISO(event.date))}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <EventIcon className={`w-4 h-4 ${eventType.color.split(' ').slice(1).join(' ')}`} strokeWidth={1.5} />
                          <span className="text-sm font-medium truncate">{event.title}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {format(parseISO(event.date), 'PPP', { locale: dateLocale })}
                          {event.time && ` • ${event.time}`}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('noData')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CalendarPage;
